import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { UpsertCompetition } from "~pages/Competitions/types/upsertCompetition.ts";
import type { Competition } from "~types/competition.ts";

export const useUpsertCompetition = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const request = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const [competitionId, setCompetitionId] = useState<string | undefined>(undefined);

  return useMutation<Competition, Error, UpsertCompetition>({
    mutationFn: async (data) => {
      const url = data.competitionId
        ? `${sport}/o/${organizationId}/competitions/${data.competitionId}`
        : `${sport}/o/${organizationId}/competitions`;
      setCompetitionId(data.competitionId);
      const { competitionId: _, ...body } = data;

      return await request(url, {
        method: data.competitionId ? "PUT" : "POST",
        body: body,
      });
    },
    onSuccess: async (createdCompetition) => {
      queryClient.setQueryData(["permission-scope-current-user", sport, organizationId], (oldScope) => {
        if (!oldScope) {
          return;
        }

        const updatedScope = JSON.parse(JSON.stringify(oldScope));
        if ("competitions" in updatedScope.resources) {
          updatedScope.resources.competitions[createdCompetition.competitionId] = createdCompetition;
        } else {
          updatedScope.resources.competitions = {
            [createdCompetition.competitionId]: createdCompetition,
          };
        }

        const leagueToUpdate = updatedScope.scope.leagueTree.find(({ leagueId }) => leagueId === createdCompetition.leagueId);
        if (leagueToUpdate) {
          leagueToUpdate.competitions.push({ competitionId: createdCompetition.competitionId, seasonIds: [] });
        }
        return updatedScope;
      });

      notification.success({
        message: formatMessage({ id: competitionId ? "competition.update.success" : "competition.create.success" }),
        placement: "bottomRight",
      });

      const promises: Promise<any>[] = [invalidateCache()];
      if (competitionId) {
        promises.push(queryClient.invalidateQueries({ queryKey: ["competitions", sport, organizationId, competitionId] }));
      }
      await Promise.all(promises);
    },
  });
};
